import { config } from '@/config';
import { DEFAULT_FONT_SIZE, WHITE_SPACE_AND_UNDERSCORE_REMOVER } from '@/constants';
import { ClassEnum, COLOR_SCHEME_OPTIONS, SETTING_TYPE } from '@/constants/enum';
import { getColor } from '@/helpers';
import { useGetPostalCodeValidationQuery } from '@/redux/query';
import { widgetColorSelector, widgetSettingSelector } from '@/redux/slice/widget.slice';
import { useMemo } from 'react';
import { useSelector } from 'react-redux';

import { PATH } from '@/constants/path';
import { handleSavePreviewOptionSettings, previewOptionSelector } from '@/redux/slice/preview.slice';
import { widgetTextDetailSelector } from '@/redux/slice/widgetText.slice';
import { useDispatch } from 'react-redux';
import { useLocation } from 'react-router-dom';
import Dropdown from '../../components/Dropdown';

const Delivery = () => {
  const widgetTextDetail = useSelector(widgetTextDetailSelector);
  const widgetColors = useSelector(widgetColorSelector);
  const widgetSettings = useSelector(widgetSettingSelector);
  const previewOptions = useSelector(previewOptionSelector);
  const { data: validationPostalCodeData } = useGetPostalCodeValidationQuery({ shop: config.shop });
  const dispatch = useDispatch();
  const location = useLocation();

  const widgetDeliveryTexts = useMemo(
    () => widgetTextDetail.texts?.find((item) => item.type === SETTING_TYPE.DELIVERY),
    [widgetTextDetail.texts],
  );

  const handleChangeInput = (code: string) => {
    dispatch(
      handleSavePreviewOptionSettings({
        inputPostalCode: code,
        isChecking: false,
      }),
    );
    let currentCode = code.trim().replace(WHITE_SPACE_AND_UNDERSCORE_REMOVER, '').toLowerCase();

    if (
      validationPostalCodeData?.data.partialCode.some((c) => {
        let pc = c.trim().replace(WHITE_SPACE_AND_UNDERSCORE_REMOVER, '').toLowerCase();
        return currentCode.startsWith(pc) || pc.startsWith(currentCode);
      }) ||
      validationPostalCodeData?.data.exactCode.includes(currentCode)
    ) {
      dispatch(
        handleSavePreviewOptionSettings({
          errorPostalCode: false,
        }),
      );
    } else {
      dispatch(
        handleSavePreviewOptionSettings({
          errorPostalCode: true,
        }),
      );
    }
  };

  const SubmitButton = () => {
    return (
      <button
        type="submit"
        id="delivery-validation-submit"
        className={ClassEnum.ZipcodeSubmitButton}
        style={{
          color: getColor(widgetSettings.colorScheme, widgetColors, 'body', 'checkButtonColor'),
          backgroundColor: getColor(widgetSettings.colorScheme, widgetColors, 'body', 'checkButtonBackground'),
          borderColor: getColor(widgetSettings.colorScheme, widgetColors, 'body', 'checkButtonBorder'),
          borderRadius: widgetSettings.sizes.borderRadius,
        }}
        onClick={() =>
          dispatch(
            handleSavePreviewOptionSettings({
              isChecking: true,
            }),
          )
        }
      >
        {widgetDeliveryTexts?.labelAndButtons?.checkButton}
      </button>
    );
  };

  return (
    <div className={ClassEnum.Delivery} id="delivery-validation-wrapper">
      <label
        className={ClassEnum.DeliveryLabel}
        htmlFor="zip-code"
        id="delivery-label-button-instruction"
        style={{ color: widgetSettings.colorScheme === COLOR_SCHEME_OPTIONS.DARK ? '#ffffff' : '#1a1a1a' }}
      >
        {widgetDeliveryTexts?.labelAndButtons?.instructions}
      </label>
      <div className={ClassEnum.ZipcodeWrapper} id="zip-code-wrapper">
        <input
          type="text"
          name="zip-code"
          id="zip-code"
          className={ClassEnum.ZipcodeInput}
          style={{
            color: getColor(widgetSettings.colorScheme, widgetColors, 'body', 'inputColor'),
            backgroundColor: getColor(widgetSettings.colorScheme, widgetColors, 'body', 'inputBackground'),
            borderColor: getColor(widgetSettings.colorScheme, widgetColors, 'body', 'inputBorder'),
            borderRadius: widgetSettings.sizes.borderRadius,
          }}
          value={previewOptions.inputPostalCode}
          onChange={(e) => handleChangeInput(e.target.value)}
        />
        <Dropdown
          options={[
            {
              label: widgetDeliveryTexts?.labelAndButtons?.zipCodeLabel,
              value: widgetDeliveryTexts?.labelAndButtons?.zipCodeLabel || '',
            },
          ]}
          value={widgetDeliveryTexts?.labelAndButtons?.zipCodeLabel || ''}
          onChange={() => {}}
          borderRadius={widgetSettings.sizes.borderRadius}
          styles={{
            button: { height: '36px' },
            contentItem: {
              maxHeight: '40px',
              padding: '8px 6px',
              background: widgetSettings.colorScheme === COLOR_SCHEME_OPTIONS.DARK ? '#1a1a1a' : '#ffffff',
              color: widgetSettings.colorScheme === COLOR_SCHEME_OPTIONS.DARK ? '#ffffff' : '#1a1a1a',
            },
            label: {
              color: widgetSettings.colorScheme === COLOR_SCHEME_OPTIONS.DARK ? '#ffffff' : '#1a1a1a',
            },
            content: {
              background: widgetSettings.colorScheme === COLOR_SCHEME_OPTIONS.DARK ? '#1a1a1a' : '#ffffff',
              color: widgetSettings.colorScheme === COLOR_SCHEME_OPTIONS.DARK ? '#ffffff' : '#1a1a1a',
            },
          }}
        />
      </div>
      <SubmitButton />
      {previewOptions.isChecking && (!previewOptions.errorPostalCode || location.pathname === PATH.ONBOARDING.pathname) && (
        <p
          className={ClassEnum.DeliveryEligibleMessage}
          style={{
            color: getColor(widgetSettings.colorScheme, widgetColors, 'body', 'eligibleMessageColor'),
            fontSize: widgetSettings?.sizes?.errorMessageFontSize || DEFAULT_FONT_SIZE,
            lineHeight: 1.5,
            wordBreak: 'break-word',
          }}
          id="delivery-validation-success-message"
        >
          {widgetDeliveryTexts?.requiredConditions?.eligibleNotice}
        </p>
      )}
      {previewOptions.isChecking && previewOptions.errorPostalCode && location.pathname !== PATH.ONBOARDING.pathname && (
        <p
          className={ClassEnum.DeliveryErrorMessage}
          style={{
            color: getColor(widgetSettings.colorScheme, widgetColors, 'body', 'errorColor'),
            fontSize: widgetSettings?.sizes?.errorMessageFontSize || DEFAULT_FONT_SIZE,
            lineHeight: 1.5,
            wordBreak: 'break-word',
          }}
          id="delivery-validation-error-message"
        >
          {widgetDeliveryTexts?.errorMessages?.notSupportedZone}
        </p>
      )}
    </div>
  );
};

export default Delivery;
